// Tiny, dependency-free HTML syntax highlighter for the reviewer's
// "View code" panel.
//
// ## XSS safety
// The ENTIRE source is HTML-escaped first, then only our own
// <span class="tok-*"> wrappers are inserted around comments / tag names /
// attribute names / attribute string values. The escaped payload can never
// form live markup, so the result is safe for dangerouslySetInnerHTML — even
// when the source contains literal "<script>", quoted attributes, etc.
//
// ## Scope (HTML only, no JS/CSS grammar)
// Highlights HTML markup: tag names, attribute names, double- and
// single-quoted attribute values, and comments. Content inside <script> /
// <style> / <pre> stays as plain escaped text (no JS or CSS tokenizer).
// This is intentional — the reviewer panel shows widget source and the
// primary need is making the HTML structure readable.
//
// ## Known limitations (intentional, not bugs)
// - Boolean attributes without `=` (e.g. <input disabled>) are NOT
//   highlighted — the regex requires a trailing `=` to bound the match.
// - Nested `<` inside attribute values (e.g. <a title="<x>">) will mis-parse
//   because the tag boundary regex is greedy. This is a fundamental limit
//   of regex-based HTML parsing; full correctness needs a real parser and
//   would dwarf the rest of this file.
// - CDATA / DOCTYPE / processing instructions are left as escaped text.

const escapeHtml = (s: string): string =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')

// Highlight the internals of a single (already-escaped) tag in ONE pass so we
// never re-scan text we've already wrapped.
const highlightTag = (tag: string): string =>
  tag.replace(
    /("[^"]*"|'[^']*')|(&lt;\/?)([a-zA-Z][\w-]*)|([a-zA-Z_:][\w:.-]*)(?==)/g,
    (match, str, bracket, name, attr) => {
      if (str) return `<span class="tok-string">${str}</span>`
      if (name) return `${bracket}<span class="tok-tag">${name}</span>`
      if (attr) return `<span class="tok-attr">${attr}</span>`
      return match
    }
  )

// Wrap each comment or tag in a single pass. Order in the alternation
// matters: comments first so their `<!--` is not consumed as a tag open.
export const highlightHtml = (source: string): string => {
  const escaped = escapeHtml(source ?? '')
  return escaped.replace(
    /(&lt;!--[\s\S]*?--&gt;)|(&lt;\/?[a-zA-Z][^]*?&gt;)/g,
    (match, comment, tag) => {
      if (comment) return `<span class="tok-comment">${comment}</span>`
      if (tag) return highlightTag(tag)
      return match
    }
  )
}
